import { useState, useCallback, useEffect } from "react";
import { AVATARS, type AvatarId } from "@jumpbean/shared";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { Avatar } from "./Avatar";

export interface ChildData {
  id: string;
  displayName: string;
  avatarId: string;
  dateOfBirth: string | null;
}

export interface EditChildModalProps {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** The child being edited */
  child: ChildData | null;
  /** Callback when the child is successfully updated */
  onUpdate: (child: ChildData) => void;
  /** Callback when the child is deleted */
  onDelete: (childId: string) => void;
}

/**
 * Modal for editing an existing child profile.
 * Allows updating name/avatar and includes delete functionality.
 *
 * @example
 * <EditChildModal
 *   isOpen={showEditChild}
 *   onClose={() => setShowEditChild(false)}
 *   child={selectedChild}
 *   onUpdate={(updated) => updateChildInList(updated)}
 *   onDelete={(id) => removeChildFromList(id)}
 * />
 */
export function EditChildModal({
  isOpen,
  onClose,
  child,
  onUpdate,
  onDelete,
}: EditChildModalProps) {
  const [displayName, setDisplayName] = useState("");
  const [selectedAvatar, setSelectedAvatar] = useState<AvatarId | null>(null);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Reset form when child changes
  useEffect(() => {
    if (child) {
      setDisplayName(child.displayName);
      setSelectedAvatar(child.avatarId as AvatarId);
      setDateOfBirth(child.dateOfBirth ?? "");
      setError(null);
      setShowDeleteConfirm(false);
    }
  }, [child]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setError(null);
      setShowDeleteConfirm(false);
      onClose();
    }
  }, [isSubmitting, onClose]);

  const handleSubmit = async () => {
    if (!child) return;

    const trimmedName = displayName.trim();
    if (!trimmedName) {
      setError("Please enter a name");
      return;
    }
    if (trimmedName.length > 50) {
      setError("Name is too long");
      return;
    }
    if (!selectedAvatar) {
      setError("Please choose an avatar");
      return;
    }

    // Check if anything changed
    const originalDob = child.dateOfBirth ?? "";
    if (trimmedName === child.displayName && selectedAvatar === child.avatarId && dateOfBirth === originalDob) {
      handleClose();
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const response = await fetch(`/api/children/${child.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          displayName: trimmedName,
          avatarId: selectedAvatar,
          dateOfBirth: dateOfBirth || null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error?.message || "Failed to update profile");
        return;
      }

      onUpdate({
        id: data.id,
        displayName: data.displayName,
        avatarId: data.avatarId,
        dateOfBirth: data.dateOfBirth,
      });
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!child) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`/api/children/${child.id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error?.message || "Failed to delete profile");
        setShowDeleteConfirm(false);
        return;
      }

      onDelete(child.id);
    } catch (err) {
      setError("Something went wrong. Please try again.");
      setShowDeleteConfirm(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!child) return null;

  const hasChanges =
    displayName.trim() !== child.displayName ||
    selectedAvatar !== child.avatarId ||
    dateOfBirth !== (child.dateOfBirth ?? "");

  // Delete confirmation view
  if (showDeleteConfirm) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Delete Profile?"
        size="sm"
        closeOnBackdropClick={!isSubmitting}
        closeOnEscape={!isSubmitting}
      >
        <div className="flex flex-col gap-6">
          {/* Warning message */}
          <div className="text-center">
            <div className="mx-auto mb-4">
              <Avatar avatarId={child.avatarId as AvatarId} size="xl" />
            </div>
            <p className="font-display font-bold text-body-xl text-neutral-800 mb-2">
              {child.displayName}
            </p>
            <p className="font-body text-body-md text-neutral-600">
              This will remove all their progress and badges. This cannot be undone.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div
              className="
                px-4 py-3
                bg-fairy-50
                border-2 border-fairy-300
                rounded-xl
                text-center
              "
              role="alert"
            >
              <p className="font-body text-body-md text-fairy-700 font-medium">
                {error}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            <Button
              variant="secondary"
              size="lg"
              fullWidth
              onClick={handleDelete}
              isLoading={isSubmitting}
              disabled={isSubmitting}
              className="!border-fairy-400 !text-fairy-600 hover:!bg-fairy-50"
            >
              {isSubmitting ? "Deleting..." : "Yes, Delete Profile"}
            </Button>

            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isSubmitting}
            >
              No, Keep Profile
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  // Edit view
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Profile"
      size="md"
      closeOnBackdropClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <div className="flex flex-col gap-6">
        {/* Name Input */}
        <div className="space-y-2">
          <label
            htmlFor="edit-child-name"
            className="block font-display font-semibold text-body-lg text-neutral-700"
          >
            Name
          </label>
          <input
            id="edit-child-name"
            type="text"
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
              if (error) setError(null);
            }}
            placeholder="Enter name..."
            maxLength={50}
            disabled={isSubmitting}
            className="
              w-full
              px-5 py-4
              font-body text-body-lg text-neutral-800
              bg-neutral-50
              border-[3px] border-neutral-200
              rounded-2xl
              placeholder:text-neutral-400
              transition-all duration-200
              focus:outline-none focus:border-magic-400 focus:ring-4 focus:ring-magic-100
              disabled:opacity-60 disabled:cursor-not-allowed
            "
          />
        </div>

        {/* Date of Birth Input */}
        <div className="space-y-2">
          <label
            htmlFor="edit-child-dob"
            className="block font-display font-semibold text-body-lg text-neutral-700"
          >
            Birthday
          </label>
          <input
            id="edit-child-dob"
            type="date"
            value={dateOfBirth}
            onChange={(e) => {
              setDateOfBirth(e.target.value);
              if (error) setError(null);
            }}
            max={new Date().toISOString().split("T")[0]}
            disabled={isSubmitting}
            className="
              w-full
              px-5 py-4
              font-body text-body-lg text-neutral-800
              bg-neutral-50
              border-[3px] border-neutral-200
              rounded-2xl
              transition-all duration-200
              focus:outline-none focus:border-magic-400 focus:ring-4 focus:ring-magic-100
              disabled:opacity-60 disabled:cursor-not-allowed
            "
          />
          <p className="font-body text-body-sm text-neutral-500">
            This helps us tailor content to their age
          </p>
        </div>

        {/* Avatar Selection */}
        <div className="space-y-3">
          <p className="font-display font-semibold text-body-lg text-neutral-700">
            Avatar
          </p>
          <div className="grid grid-cols-4 gap-3">
            {AVATARS.map((avatar: typeof AVATARS[number]) => (
              <Avatar
                key={avatar.id}
                avatarId={avatar.id}
                size="lg"
                isSelectable
                isSelected={selectedAvatar === avatar.id}
                onSelect={() => {
                  setSelectedAvatar(avatar.id);
                  if (error) setError(null);
                }}
                className="mx-auto"
              />
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            className="
              px-4 py-3
              bg-fairy-50
              border-2 border-fairy-300
              rounded-xl
              text-center
            "
            role="alert"
          >
            <p className="font-body text-body-md text-fairy-700 font-medium">
              {error}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 pt-2">
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleSubmit}
            isLoading={isSubmitting}
            disabled={!hasChanges || isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </Button>

          <Button
            variant="ghost"
            size="md"
            fullWidth
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </div>

        {/* Delete Button - Separated */}
        <div className="pt-2 border-t border-neutral-200">
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isSubmitting}
            className="
              w-full
              py-3
              font-body text-body-md text-neutral-500
              hover:text-fairy-600
              transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed
              focus:outline-none focus-visible:text-fairy-600
            "
          >
            Delete Profile
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default EditChildModal;
